import { Injectable, inject } from '@angular/core';
import { UtmStore } from '@partners/state';
import { queryStringToObject } from '@partners/utils';
import { Observable, map, of, tap } from 'rxjs';
import { IUtmConfig, IUtmEdaModel, IUtmEdaModelData, IUtmModel, IUtmParams } from '../interface';
import { UTM_CONFIG, UTM_PARAMS } from '../tokens';

@Injectable({
  providedIn: 'root'
})
export class UtmService {
  #utmConfig: IUtmConfig = inject(UTM_CONFIG);
  #utmParams: IUtmParams[] = inject(UTM_PARAMS);
  #utmStoreFacade: UtmStore = inject(UtmStore);

  constructor() {}

  buildUtmModel(): IUtmModel {
    const { utmSource, utmMedium, utmCampaign, utmTerm, utmContent } = this.#utmStoreFacade.utmParams$$();

    return {
      utmSource,
      utmMedium,
      utmCampaign,
      utmTerm,
      utmContent
    } as IUtmModel;
  }

  getEdaUtmUrl(): string {
    return this.#utmConfig.calEdaUri;
  }

  buildDigitalClientData(idNumber: string, barcode: string, opportunidyId: number): IUtmEdaModel {
    const loanCalsaleProcessType = ['LoanCalSale'];
    const { utmSource, utmMedium, utmCampaign, utmTerm, utmContent } = this.#utmStoreFacade.utmParams$$();

    return {
      data: [
        { utm_source: utmSource },
        { utm_medium: utmMedium },
        { utm_campaign: utmCampaign },
        { utm_term: utmTerm },
        { utm_content: utmContent },
        { barcode },
        { opportunidyID: opportunidyId }
      ],
      eventdate: new Date().toJSON(),
      googleClientId: this.getClientId(),
      processType: loanCalsaleProcessType,
      userId: idNumber
    } as IUtmEdaModel;
  }

  loadUtmParams$(rawUrl: string = window.location.href): Observable<IUtmModel> {
    return this.parseUtmParams$(rawUrl).pipe(tap((utmParams) => this.#utmStoreFacade.dispatchUtmLoad(utmParams)));
  }

  /**
   * @description here we will add utm values to a given url.
   * the purpose of doing it is if we navigate to another site (for example ecredit)
   * we need to add the utms so previously navigated site will be able to save and record the utms
   * used to find it.
   * @param {*} url
   * @returns a given url with utms value.
   */
  addUtmToUrl(url: string): string {
    const { utmSource, utmMedium, utmCampaign, utmTerm, utmContent } = this.#utmStoreFacade.utmParams$$();

    let modifiedUrl = url;

    modifiedUrl += `&utm_source=${utmSource}`;
    modifiedUrl += `&utm_medium=${utmMedium}`;
    modifiedUrl += `&utm_campaign=${utmCampaign}`;
    modifiedUrl += `&utm_term=${utmTerm}`;
    modifiedUrl += `&utm_content=${utmContent}`;

    return modifiedUrl;
  }

  private parseUtmParams$(url: string): Observable<IUtmModel> {
    return of(url).pipe(
      map((url) => {
        /** read whatever params are in the querystring */
        const urlParams = queryStringToObject(this.#utmParams, url) as IUtmEdaModelData;

        const utmModel: IUtmModel = {
          utmSource: urlParams.utm_source,
          utmMedium: urlParams.utm_medium,
          utmCampaign: urlParams.utm_campaign,
          utmTerm: urlParams.utm_term,
          utmContent: urlParams.utm_content
        };

        return utmModel;
      })
    );
  }

  /**
   * @description
   * extract google client id value from cookies
   * @returns googleClientId value
   */
  private getClientId(): string | undefined {
    try {
      const cookieString = document.cookie;
      const cookies = cookieString?.split('; ');

      if (!cookies?.length) {
        return undefined;
      }
      return cookies.reduce((_, cookie) => {
        const cookieArray = cookie.split('=');
        if (cookieArray?.length && cookieArray[0] === '_ga') {
          return cookieArray[1];
        }
        return _;
      }, undefined);
    } catch (err) {
      return '';
    }
  }
}
